import { z } from 'zod'
import { organizationIdSchema } from '@/lib/api/contracts/primitives'
import { defineRouteContract } from '@/lib/api/contracts/types'
import {
  permissionGroupConfigSchema,
  permissionGroupReadShape,
} from '@/lib/permission-groups/fields'

/**
 * The wire shape of a resolved config: every key present, in registry order.
 *
 * Built from the same field registry as the write schema and the tolerant
 * parser, because the group editor's dirty check compares stringified configs —
 * a key this schema omitted, or ordered differently, would read as an unsaved
 * change forever.
 */
export const permissionGroupFullConfigSchema = z.object(permissionGroupReadShape)

export const addPermissionGroupMemberBodySchema = z.object({
  userId: z.string().min(1, 'userId is required'),
})

/** Route params for organization-scoped permission-group collection routes (`id` = organizationId). */
export const permissionGroupParamsSchema = z.object({
  id: organizationIdSchema,
})
export type PermissionGroupParams = z.input<typeof permissionGroupParamsSchema>

/** Route params for a single permission group (`id` = organizationId, `groupId` = permission group id). */
export const permissionGroupDetailParamsSchema = z.object({
  id: organizationIdSchema,
  groupId: z.string().min(1),
})
export type PermissionGroupDetailParams = z.input<typeof permissionGroupDetailParamsSchema>

/** A workspace a permission group targets (id + display name). */
export const permissionGroupWorkspaceRefSchema = z.object({
  id: z.string(),
  name: z.string(),
})
export type PermissionGroupWorkspaceRef = z.output<typeof permissionGroupWorkspaceRefSchema>

export const permissionGroupSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  config: permissionGroupFullConfigSchema,
  createdBy: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  creatorName: z.string().nullable(),
  creatorEmail: z.string().nullable(),
  memberCount: z.number(),
  isDefault: z.boolean(),
  /**
   * Workspaces this group targets. Empty for the default group (which governs
   * every workspace) and for a non-default group scoped to nothing.
   */
  workspaces: z.array(permissionGroupWorkspaceRefSchema),
})
export type PermissionGroup = z.output<typeof permissionGroupSchema>

export const permissionGroupWriteSchema = z.object({
  id: z.string(),
  organizationId: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  config: permissionGroupFullConfigSchema,
  createdBy: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  isDefault: z.boolean(),
  /** Ids of targeted workspaces (empty for the default group). */
  workspaceIds: z.array(z.string()),
})
export type PermissionGroupWrite = z.output<typeof permissionGroupWriteSchema>

export const permissionGroupMemberSchema = z.object({
  id: z.string(),
  userId: z.string(),
  assignedAt: z.string(),
  userName: z.string().nullable(),
  userEmail: z.string().nullable(),
  userImage: z.string().nullable(),
})
export type PermissionGroupMember = z.output<typeof permissionGroupMemberSchema>

export const userPermissionConfigQuerySchema = z.object({
  workspaceId: z.string().min(1),
})

export const userPermissionConfigSchema = z.object({
  permissionGroupId: z
    .string()
    .nullable()
    .describe('Identifier of the group governing the caller; null when no group applies.'),
  groupName: z.string().nullable().describe('Name of the governing permission group.'),
  config: permissionGroupFullConfigSchema
    .nullable()
    .describe(
      'Effective group restrictions. True disables a boolean capability; null allowlists allow all values and empty allowlists allow none. Null config means no group applies.'
    ),
  entitled: z.boolean().describe('Whether organization permission governance is active.'),
  organizationId: z
    .string()
    .nullable()
    .describe('Organization that owns the workspace; null for a personal workspace.'),
  isOrgAdmin: z
    .boolean()
    .describe('Whether the caller is an owner or administrator of the workspace’s organization.'),
})
export type UserPermissionConfig = z.output<typeof userPermissionConfigSchema>

/** Upper bound on how many workspaces a single group can explicitly target. */
export const MAX_PERMISSION_GROUP_WORKSPACES = 500

const workspaceIdsSchema = z.array(z.string().min(1)).max(MAX_PERMISSION_GROUP_WORKSPACES)

/**
 * The one cross-field scope rule shared by create and update: the organization
 * default group governs every workspace, so it cannot also name specific
 * workspaces (they would be silently dropped server-side). "Org-wide" is
 * definitionally `isDefault` — there is no separate flag — so a default group
 * with no `workspaceIds` is already the all-workspaces case and needs no
 * assertion here.
 *
 * Other scope rules are enforced by the shared manager: a non-default group targets the
 * workspaces in `workspaceIds` (empty is allowed on update — the group then
 * governs nothing, since the resolver inner-joins the workspace link table), and
 * creation requires at least one workspace up front.
 */
function refineWorkspaceScope(
  body: { workspaceIds?: string[]; isDefault?: boolean },
  ctx: z.RefinementCtx
) {
  if (body.isDefault === true && body.workspaceIds && body.workspaceIds.length > 0) {
    ctx.addIssue({
      code: 'custom',
      path: ['workspaceIds'],
      message: 'The default group governs all workspaces and cannot target specific workspaces',
    })
  }
}

export const createPermissionGroupBodySchema = z
  .object({
    name: z
      .string({ error: 'name is required' })
      .trim()
      .min(1, 'name is required')
      .max(100, 'name cannot exceed 100 characters')
      .describe('Group name, unique within the organization.'),
    description: z
      .string()
      .trim()
      .max(500, 'description cannot exceed 500 characters')
      .optional()
      .describe('Optional group description.'),
    config: permissionGroupConfigSchema.optional(),
    isDefault: z
      .boolean()
      .optional()
      .describe(
        'Whether the group is the organization default. Only one group can be the default.'
      ),
    workspaceIds: workspaceIdsSchema
      .optional()
      .describe(
        'Workspace identifiers for a non-default group. Required on creation; an empty update makes the group inactive.'
      ),
  })
  .superRefine(refineWorkspaceScope)
export type CreatePermissionGroupBody = z.input<typeof createPermissionGroupBodySchema>

export const updatePermissionGroupBodySchema = z
  .object({
    name: z
      .string({ error: 'name is required' })
      .trim()
      .min(1, 'name is required')
      .max(100, 'name cannot exceed 100 characters')
      .describe('Group name, unique within the organization.')
      .optional(),
    description: z
      .string()
      .trim()
      .max(500, 'description cannot exceed 500 characters')
      .nullable()
      .optional()
      .describe(
        'Group description. Null or an empty string clears it; omission leaves it unchanged.'
      ),
    config: permissionGroupConfigSchema.optional(),
    isDefault: z
      .boolean()
      .optional()
      .describe(
        'Whether the group is the organization default. Only one group can be the default.'
      ),
    workspaceIds: workspaceIdsSchema
      .optional()
      .describe(
        'Workspace identifiers for a non-default group. Required on creation; an empty update makes the group inactive.'
      ),
  })
  .superRefine(refineWorkspaceScope)
export type UpdatePermissionGroupBody = z.input<typeof updatePermissionGroupBodySchema>

export const removePermissionGroupMemberQuerySchema = z.object({
  memberId: z.string().min(1),
})
export type RemovePermissionGroupMemberQuery = z.input<
  typeof removePermissionGroupMemberQuerySchema
>

export const bulkAddPermissionGroupMembersBodySchema = z.object({
  userIds: z.array(z.string()).optional(),
  addAllOrganizationMembers: z.boolean().optional(),
})
export type BulkAddPermissionGroupMembersBody = z.input<
  typeof bulkAddPermissionGroupMembersBodySchema
>

const successResponseSchema = z.object({
  success: z.literal(true),
})

export const listPermissionGroupsContract = defineRouteContract({
  method: 'GET',
  path: '/api/organizations/[id]/permission-groups',
  params: permissionGroupParamsSchema,
  response: {
    mode: 'json',
    schema: z.object({
      permissionGroups: z.array(permissionGroupSchema).optional(),
    }),
  },
})

export const createPermissionGroupContract = defineRouteContract({
  method: 'POST',
  path: '/api/organizations/[id]/permission-groups',
  params: permissionGroupParamsSchema,
  body: createPermissionGroupBodySchema,
  response: {
    mode: 'json',
    schema: z.object({
      permissionGroup: permissionGroupWriteSchema,
    }),
    status: 201,
  },
})

export const getUserPermissionConfigContract = defineRouteContract({
  method: 'GET',
  path: '/api/permission-groups/user',
  query: userPermissionConfigQuerySchema,
  response: {
    mode: 'json',
    schema: userPermissionConfigSchema,
  },
})

export const getPermissionGroupContract = defineRouteContract({
  method: 'GET',
  path: '/api/organizations/[id]/permission-groups/[groupId]',
  params: permissionGroupDetailParamsSchema,
  response: {
    mode: 'json',
    schema: z.object({
      permissionGroup: permissionGroupWriteSchema.omit({ workspaceIds: true }).extend({
        membershipMode: z.string(),
        workspaces: z.array(permissionGroupWorkspaceRefSchema),
      }),
    }),
  },
})

export const updatePermissionGroupContract = defineRouteContract({
  method: 'PUT',
  path: '/api/organizations/[id]/permission-groups/[groupId]',
  params: permissionGroupDetailParamsSchema,
  body: updatePermissionGroupBodySchema,
  response: {
    mode: 'json',
    schema: z.object({
      permissionGroup: permissionGroupWriteSchema,
    }),
  },
})

export const deletePermissionGroupContract = defineRouteContract({
  method: 'DELETE',
  path: '/api/organizations/[id]/permission-groups/[groupId]',
  params: permissionGroupDetailParamsSchema,
  response: {
    mode: 'json',
    schema: successResponseSchema,
  },
})

export const listPermissionGroupMembersContract = defineRouteContract({
  method: 'GET',
  path: '/api/organizations/[id]/permission-groups/[groupId]/members',
  params: permissionGroupDetailParamsSchema,
  response: {
    mode: 'json',
    schema: z.object({
      members: z.array(permissionGroupMemberSchema).optional(),
    }),
  },
})

export const removePermissionGroupMemberContract = defineRouteContract({
  method: 'DELETE',
  path: '/api/organizations/[id]/permission-groups/[groupId]/members',
  params: permissionGroupDetailParamsSchema,
  query: removePermissionGroupMemberQuerySchema,
  response: {
    mode: 'json',
    schema: successResponseSchema,
  },
})

export const addPermissionGroupMemberContract = defineRouteContract({
  method: 'POST',
  path: '/api/organizations/[id]/permission-groups/[groupId]/members',
  params: permissionGroupDetailParamsSchema,
  body: addPermissionGroupMemberBodySchema,
  response: {
    mode: 'json',
    schema: z.object({
      member: z.object({
        id: z.string(),
        permissionGroupId: z.string(),
        organizationId: z.string(),
        userId: z.string(),
        assignedBy: z.string(),
        assignedAt: z.string(),
      }),
    }),
    status: 201,
  },
})

export const bulkAddPermissionGroupMembersContract = defineRouteContract({
  method: 'POST',
  path: '/api/organizations/[id]/permission-groups/[groupId]/members/bulk',
  params: permissionGroupDetailParamsSchema,
  body: bulkAddPermissionGroupMembersBodySchema,
  response: {
    mode: 'json',
    schema: z.object({
      added: z.number(),
      // Users not added because they were already in this group. A conflicting
      // selection fails the whole request (409) rather than being skipped, so
      // the add is all-or-nothing for conflicts.
      skipped: z.number(),
    }),
  },
})

/**
 * List the workspaces belonging to an organization, used to populate the
 * workspace multi-select when scoping a permission group to specific workspaces.
 */
export const listOrganizationWorkspacesContract = defineRouteContract({
  method: 'GET',
  path: '/api/organizations/[id]/workspaces',
  params: permissionGroupParamsSchema,
  response: {
    mode: 'json',
    schema: z.object({
      workspaces: z.array(permissionGroupWorkspaceRefSchema),
    }),
  },
})
